import moment from "moment";
import { useMemo, useState } from "react";
import { Calendar } from "src/components/calendar/Calendar";
import { AttendanceType } from "src/redux/types/_attendance.type";
import { useParams } from "react-router-dom";
import AttendanceOnDateCard from "./AttendanceOnDateCard";
import { useFetchHolidays } from "./hooks/useFetchHolidays";
import { useAttendanceByEmployeeId } from "./hooks/useAttendanceByEmployeeId";
import { useLeaveDays } from "./hooks/useLeaveDays";
import LeaveCalendarCard from "./LeaveCalendarCard";

type AttendanceHistoryProps = {
  selectedDate: Date | undefined;
  onDateSelect: (date: Date | undefined) => void;
  onAttendanceSelect: (id: string | null) => void;
};

const AttendanceHistory = ({
  selectedDate,
  onDateSelect,
  onAttendanceSelect,
}: AttendanceHistoryProps) => {
  const { empId } = useParams();
  const employeeId = Number(empId);

  const { holidays, loading: isHolidayLoading } = useFetchHolidays();

  const { data: leaveDays = [] } = useLeaveDays({ id: employeeId });

  const {
    data: records = [],
    isLoading,
    isError,
  } = useAttendanceByEmployeeId(employeeId);

  const [selectedRecord, setSelectedRecord] = useState<AttendanceType | null>(
    null,
  );
  const [selectedHoliday, setSelectedHoliday] = useState<string | null>(null);
  const [leaveType, setLeaveType] = useState<string | null>(null);

  const leaveDates = useMemo(
    () =>
      leaveDays.map((leave) => ({
        date: new Date(leave.date),
        type: leave.type,
      })),
    [leaveDays],
  );

  const leaveDateKeys = useMemo(
    () => new Set(leaveDates.map((l) => l.date.toDateString())),
    [leaveDates],
  );

  const isOnLeave = (date: Date) => leaveDateKeys.has(date.toDateString());

  const presentDates = useMemo(
    () =>
      records
        .filter((a) => a.attendance_status === "FULLDAY")
        .map((a) => new Date(a.date)),
    [records],
  );

  const absentDates = useMemo(
    () =>
      records
        .filter((a) => a.attendance_status === "ABSENT")
        .map((a) => new Date(a.date))
        .filter((d) => !isOnLeave(d)),
    [records, leaveDateKeys]
  );

  const halfday = useMemo(
    () =>
      records
        .filter((a) => a.attendance_status === "HALFDAY")
        .map((a) => new Date(a.date)),
    [records],
  );

  const holidayDates = useMemo(
    () =>
      holidays.map((h: any) => ({
        date: new Date(h.date),
        occasion: h.occasion,
      })),
    [holidays],
  );

  const leaveLWP = useMemo(
    () => leaveDates.filter((l) => l.type === "LWP").map((l) => l.date),
    [leaveDates],
  );

  const leaveMTL = useMemo(
    () => leaveDates.filter((l) => l.type === "MTL").map((l) => l.date),
    [leaveDates],
  );

  const leavePTL = useMemo(
    () => leaveDates.filter((l) => l.type === "PTL").map((l) => l.date),
    [leaveDates],
  );

  const leaveSL = useMemo(
    () => leaveDates.filter((l) => l.type === "SL").map((l) => l.date),
    [leaveDates],
  );

  const leaveCL = useMemo(
    () => leaveDates.filter((l) => l.type === "CL").map((l) => l.date),
    [leaveDates],
  );

  const leavePLL = useMemo(
    () => leaveDates.filter((l) => l.type === "PLL").map((l) => l.date),
    [leaveDates],
  );

  const handleDateSelect = (date: Date | undefined) => {
    onDateSelect(date);

    if (!date) {
      setSelectedRecord(null);
      setSelectedHoliday(null);
      onAttendanceSelect(null);
      return;
    }

    // Match attendance by date (ignoring time)
    const matched = records.find((record) => {
      const recordDate = new Date(record.date);
      return (
        recordDate.getFullYear() === date.getFullYear() &&
        recordDate.getMonth() === date.getMonth() &&
        recordDate.getDate() === date.getDate()
      );
    });

    setSelectedRecord(matched || null);
    onAttendanceSelect(matched?.id ?? null);

    const matchedHoliday = holidayDates.find(
      (h) => h.date.toDateString() === date.toDateString(),
    );
    setSelectedHoliday(matchedHoliday ? matchedHoliday.occasion : null);

    const leaveday = leaveDates.find(
      (l) => l.date.toDateString() === date.toDateString(),
    );
    setLeaveType(leaveday ? leaveday.type : null);
  };

  return (
    <div className="flex justify-center items-center flex-col gap-4">
      {(isLoading || isHolidayLoading) && (
        <p className="text-sm text-muted">Loading Attendance...</p>
      )}
      {isError && (
        <p className="text-red-500 text-sm">Failed to load attendance data</p>
      )}
      <div className="w-full p-3 rounded-xl bg-card shadow-md">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          className="rounded-md border shadow-sm"
          modifiers={{
            present: presentDates,
            absent: absentDates,
            halfday: halfday,
            holiday: holidayDates.map((h) => h.date),
            leaveLWP: leaveLWP,
            leaveMTL: leaveMTL,
            leavePTL: leavePTL,
            leaveSL: leaveSL,
            leaveCL: leaveCL,
            leavePLL: leavePLL,
          }}
          modifiersClassNames={{
            present: "bg-green-100 text-green-700 rounded-full",
            absent: "bg-red-100 text-red-700 rounded-full",
            halfday: "bg-amber-100 text-amber-700 rounded-full",
            holiday: "bg-slate-100 text-slate-700 font-semibold rounded-full",
            leaveLWP:
              "bg-orange-100 text-orange-700 font-semibold rounded-full",
            leaveMTL: "bg-pink-100 text-pink-700 font-semibold rounded-full",
            leavePTL:
              "bg-indigo-100 text-indigo-700 font-semibold rounded-full",
            leaveSL: "bg-stone-300 text-stone-700 font-semibold rounded-full",
            leaveCL:
              "bg-fuchsia-100 text-fuchsia-700 font-semibold rounded-full",
            leavePLL: "bg-cyan-100 text-cyan-700 font-semibold rounded-full",
          }}
        />
      </div>

      {selectedHoliday && (
        <div className="text-purple-700 font-semibold bg-purple-50 border border-purple-200 px-4 py-2 rounded-lg">
          🎉 {selectedHoliday}
        </div>
      )}

      {selectedRecord && !leaveType ? (
        <AttendanceOnDateCard selectedRecord={selectedRecord} />
      ) : selectedDate ? (
        <div className="text-gray-500 text-md bg-card rounded-xl p-3">
          <h4>
            No attendance record for {moment(selectedDate).format("MMM Do YY")}.
          </h4>
        </div>
      ) : null}

      {leaveType && (
        <LeaveCalendarCard leaveDay={leaveType} date={selectedDate} />
      )}
    </div>
  );
};

export default AttendanceHistory;
